// import classNames from "classnames";
import { Trans, useTranslation } from "react-i18next";
import {
  createSearchParams,
  Link,
  useLocation,
  useNavigate,
  useOutletContext,
} from "react-router-dom";
import Logo from "assets/logo.jpg";
import LocationIcon from "assets/icons/location.svg";
import { getQueryParams, useCurrentLocation } from "lib/utils";
// import { COUNTRY_NAME } from "consts/query-params";
import PrivacyIcon from "assets/privacy-icon.svg";
import useWhen from "hooks/useWhen";
import { INTERCOM_ARGS, END_OF_PILOT_25_ARGS } from "consts/feature-config";

const LocationButton = ({ setModalOpen }) => {
  const { location } = useCurrentLocation();

  return (
    <div
      onClick={() => setModalOpen(true)}
      className="rounded-full cursor-pointer bg-[#0000008e] hover:bg-[#000000b1] select-none inline-flex items-center justify-center whitespace-nowrap font-medium px-x-large py-regular leading-7"
    >
      <img src={LocationIcon} alt="Location Icon" className="mr-2 -ml-1" />
      {location.name}
    </div>
  );
};

export const Footer = ({ children, className }) => {
  // translation
  const { t } = useTranslation();

  const { setShowLocationModal } = useOutletContext();

  // After the end of the pilot Intercom is not booted (see intercom-provider),
  // so render the plain mailto contact link instead of the launcher text
  const endOfPilot25 = useWhen(END_OF_PILOT_25_ARGS);
  const showIntercom = useWhen(INTERCOM_ARGS) && !endOfPilot25;

  // const showImpressum =
  //   currentLocation && currentLocation[COUNTRY_NAME] === "DE";

  // Get query params
  const location = useLocation();
  const queryParams = getQueryParams(location.search);

  const navigate = useNavigate();

  const navigateWithParams = (href, settings) => {
    const navigateOptions = { pathname: href };
    if (queryParams) {
      navigateOptions.search = createSearchParams(queryParams).toString();
    }
    if (settings?.hash) {
      navigateOptions.hash = settings.hash;
    }
    navigate(navigateOptions);
  };

  return (
    <footer className="bg-[#1f1d48] px-xxx-large py-xxx-large text-white z-30 relative">
      {/* on mobile */}
      <div className="flex w-full justify-between md:hidden">
        <img src={Logo} alt="logo" className="w-12 h-12 aspect-square" />
        <LocationButton setModalOpen={setShowLocationModal} />
      </div>
      {/* on desktop */}
      <div className="flex justify-between">
        <div className="flex flex-col md:flex-row space-y-xxx-large md:space-y-0 md:space-x-xxxx-large">
          <img
            src={Logo}
            alt="LEGO Logo"
            className="w-[80px] h-[80px] hidden md:block"
          />
          <div className="flex flex-col space-y-large">
            <div className="uppercase">{t("footer.information")}</div>
            <div className="flex flex-col space-y-medium text-gray-300">
              <div
                onClick={() =>
                  navigateWithParams(t("urls.preparation_guidelines"), {
                    hash: "packagingGuidelines",
                  })
                }
                className="hover:underline cursor-pointer"
              >
                Preparation Guidelines
              </div>
              <div
                onClick={() => navigateWithParams(t("urls.how_it_works"))}
                className="hover:underline cursor-pointer"
              >
                {t("footer.how_it_works")}
              </div>
              <div
                onClick={() =>
                  navigateWithParams(t("urls.terms_and_conditions"))
                }
                className="hover:underline cursor-pointer"
              >
                Terms and Conditions
              </div>
              <div
                onClick={() => navigateWithParams(t("urls.privacy_policy"))}
                className="hover:underline cursor-pointer"
              >
                {t("footer.privacy")}
              </div>
              <div
                onClick={() => navigateWithParams(t("urls.cookie_policy"))}
                className="hover:underline cursor-pointer"
              >
                {t("footer.cookie")}
              </div>
              <button className="hover:underline cursor-pointer flex items-center cky-banner-element">
                <img
                  src={PrivacyIcon}
                  alt="Privacy Icon"
                  className="mr-2 w-7"
                />{" "}
                Your Privacy Choices
              </button>
            </div>
          </div>
          <div className="flex flex-col space-y-large">
            <div className="uppercase">{t("footer.support")}</div>
            <div className="flex flex-col space-y-medium text-gray-300">
              <div className="flex gap-2 align-baseline group cursor-pointer whitespace-nowrap my_custom_launcher">
                {showIntercom ? (
                  <>
                    <span className="material-symbols-outlined">
                      contact_support
                    </span>
                    <Trans
                      i18nKey="ui.header_need_support"
                      components={{
                        1: (
                          <a
                            className="cursor-pointer text-gray-300"
                            href="mailto:support@legobricktakeback.com"
                          >
                            Contact us
                          </a>
                        ),
                      }}
                    />
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined">
                      contact_support
                    </span>
                    <a
                      className="cursor-pointer text-gray-300"
                      href="mailto:support@legobricktakeback.com"
                    >
                      Contact us
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="hidden md:block">
          <LocationButton setModalOpen={setShowLocationModal} />
        </div>
      </div>
      <div className="text-sm font-light mt-xxx-large text-blue-300">
        <p className="mt-small">
          {/* <Trans
            i18nKey={"footer.legal"}
            components={{
              1: (
                <Link
                  to={t("urls.terms_and_conditions")}
                  className="cursor-pointer underline"
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
            }}
          /> */}
          Copyright ©{new Date().getFullYear()}. Pentatonic Ltd., Pentatonic
          GmbH and Infinitum Technologies Inc. are licensed by The LEGO Group.
          LEGO, the LEGO logo and the Minifigure are trademarks of the LEGO
          Group. ©{new Date().getFullYear()} The LEGO Group. All rights
          reserved. UPS and{" "}
          <span>
            The{"\u00A0"}UPS{"\u00A0"}Store
          </span>{" "}
          are trademarks of United Parcel Service of America, Inc., and are used
          with permission of the owner. All rights reserved. All rights
          reserved. Any use of the services on this site is subject to the{" "}
          <Link
            to={t("urls.terms_and_conditions")}
            className="cursor-pointer underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms and Conditions
          </Link>
          .
        </p>
      </div>
    </footer>
  );
};
